/// <reference path="./types/chartjs.d.ts" />

import { Chart } from 'ChartV4';

/**
 * Theme switcher from:
 * - https://ryanfeigenbaum.com/dark-mode/
 * - https://github.com/royalfig/dark-mode-demo
 *
 * Local storage vs cookies:
 * https://stackoverflow.com/questions/3220660/local-storage-vs-cookies
 *
 * Check device light/dark mode:
 * https://usefulangle.com/post/318/javascript-check-dark-mode-activated
 */

function setThemeLight() {
	document.documentElement.setAttribute("data-color-mode", "light");
	setChartTheme(globalThis.skillLangchart);
	for (const key in globalThis) {
		if (globalThis.hasOwnProperty(key) && key.startsWith("classificationChart-")) {
			setChartTheme(globalThis[key]);
		}
	}
	(document.getElementById("prism-theme") as any).href = "https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/themes/prism-solarizedlight.min.css";
}

function setThemeDark() {
	document.documentElement.setAttribute("data-color-mode", "dark");
	setChartTheme(globalThis.skillLangchart);
	for (const key in globalThis) {
		if (globalThis.hasOwnProperty(key) && key.startsWith("classificationChart-")) {
			setChartTheme(globalThis[key]);
		}
	}
	(document.getElementById("prism-theme") as any).href = "https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/themes/prism-tomorrow.min.css";
}

function saveThemeLight() {
	localStorage.setItem("color-mode", "light");
}

function saveThemeDark() {
	localStorage.setItem("color-mode", "dark");
}

export function setUpThemeSwitcher() {
	if (localStorage.getItem("color-mode") === "dark" ||
		(window.matchMedia("(prefers-color-scheme: dark)").matches &&
		!localStorage.getItem("color-mode"))) {
		setThemeDark();
	}

	if (window.CSS && CSS.supports("color", "var(--font-color)")) {
		document.querySelectorAll(".color-mode button").forEach(function(btn) {
			btn.addEventListener("click", function(e) {
				const currentTarget = e.currentTarget as HTMLButtonElement;
				if (currentTarget.classList.contains("light-hidden")) {
					setThemeLight();
					saveThemeLight();
				} else {
					setThemeDark();
					saveThemeDark();
				}
				e.preventDefault();
				e.stopPropagation();
			});
		});
	} else {
		// If the feature isn't supported, remove the theme switcher
		var btnContainer = document.querySelector(".color-mode");
		btnContainer.remove();
	}
}

export function setChartTheme(chart: Chart) {
	if (chart) {
		const rootStyle = getComputedStyle(document.documentElement);
		const colors = {
			defaultBg: rootStyle.getPropertyValue('--chart-bg-color').trim(),
			defaultBorder: rootStyle.getPropertyValue('--chart-border-color').trim(),
			highlightBg: 'rgba(99, 255, 132, 0.2)',
			highlightBorder: 'rgba(99, 255, 132, 1)',
			text: rootStyle.getPropertyValue('--chart-text-color').trim(),
			grid: rootStyle.getPropertyValue('--chart-grid-color').trim(),
			font: rootStyle.getPropertyValue('--chart-font-family').trim(),
			fontSize: parseInt(rootStyle.getPropertyValue('--chart-font-size').trim()),
		};

		chart.options.plugins.title.color = colors.text;
		chart.options.plugins.legend.labels.color = colors.text;

		chart.options?.scales?.y?.grid && (chart.options.scales.y.grid.color = colors.grid);
		chart.options?.scales?.y?.ticks && (chart.options.scales.y.ticks.color = colors.text);
		(chart.options.scales?.y as any)?.title && ((chart.options.scales.y as any).title.color = colors.text);

		chart.options?.scales?.x?.grid && (chart.options.scales.x.grid.color = colors.grid);
		chart.options?.scales?.x?.ticks && (chart.options.scales.x.ticks.color = colors.text);
		(chart.options.scales?.x as any)?.title && ((chart.options.scales.x as any).title.color = colors.text);

		if ((chart.config as any).type === 'bar') {
			updateChartColors(chart, 'backgroundColor', '--chart-highlight-bg-color');
			updateChartColors(chart, 'borderColor', '--chart-highlight-border-color');
		}

		chart.update();
	}
}

function updateChartColors(chart: Chart, datasetProperty: string, cssVar: string) {
	const { lightAttr, darkAttr } = getCSSVarAttribute(cssVar);
	(chart.data.datasets[0] as any)[datasetProperty].forEach((color: string, index: number) => {
		if (document.documentElement.getAttribute("data-color-mode") === 'light') {
			if ((chart.data.datasets[0] as any)[datasetProperty][index] === darkAttr) {
				(chart.data.datasets[0] as any)[datasetProperty][index] = lightAttr;
			}
		} else {
			if ((chart.data.datasets[0] as any)[datasetProperty][index] === lightAttr) {
				(chart.data.datasets[0] as any)[datasetProperty][index] = darkAttr;
			}
		}
	});
}

function getCSSVarAttribute(attr: string) {
	const root = document.documentElement; // Get the root element

	// Store the current mode to restore it later
	const currentMode = root.getAttribute('data-color-mode') || 'light';

	// Create a function to get the value for a specific mode
	const getColorForMode = (mode: 'light' | 'dark') => {
		// Temporarily change the color mode
		root.setAttribute('data-color-mode', mode);
		// Get the value of the CSS variable for the specific mode
		return getComputedStyle(root).getPropertyValue(attr).trim();
	};

	// Get values for both light and dark modes
	const lightAttr = getColorForMode('light');
	const darkAttr = getColorForMode('dark');

	// Restore the original mode
	root.setAttribute('data-color-mode', currentMode);

	// Return both values
	return { lightAttr, darkAttr };
}